import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
    RiArrowLeftLine,
    RiMapPinLine,
    RiBox3Line,
    RiFileCopyLine,
    RiBankCard2Line,
    RiCloseLine,
} from 'react-icons/ri'
import { useOrder, useCancelOrder } from '@/api/useOrders'
import type { OrderItemDetail } from '@/api/useOrders'
import { OrderStatusBadge } from '@/components/orders/OrderStatusBadge/OrderStatusBadge'
import { OrderProgressBar } from '@/components/orders/OrderProgressBar'
import { OrderTimeline } from '@/components/orders/OrderTimeline/OrderTimeline'
import { Button, Spinner } from '@/components/ui'
import { ROUTES } from '@/constants/routes'
import styles from './OrderDetail.module.css'

function ItemCard({ item }: { item: OrderItemDetail }) {
    const qty = item.quantity_millares === 1 ? '1 millar' : `${item.quantity_millares} millares`
    return (
        <div className={styles.itemCard}>
            <div className={styles.itemHeader}>
                <span className={styles.itemName}>{item.project_name}</span>
                <span className={styles.itemSubtotal}>S/ {Number(item.subtotal).toFixed(2)}</span>
            </div>
            <p className={styles.itemMeta}>
                {item.material_name} · {item.size_name} · {qty}
            </p>
        </div>
    )
}

export function OrderDetail() {
    const { uuid } = useParams<{ uuid: string }>()
    const navigate = useNavigate()
    const { data: order, isLoading } = useOrder(uuid || '')
    const cancelOrder = useCancelOrder()
    const [showCancelConfirm, setShowCancelConfirm] = useState(false)

    if (isLoading) {
        return <div className={styles.loading}><Spinner size="lg" /></div>
    }

    if (!order) {
        return (
            <div className={styles.error}>
                <p>Pedido no encontrado</p>
                <button onClick={() => navigate(ROUTES.orders)}>Volver a pedidos</button>
            </div>
        )
    }

    const formatSoles = (amount: string | number) => `S/ ${Number(amount).toFixed(2)}`
    const itemCount = order.items.length
    const itemLabel = itemCount === 1 ? '1 producto' : `${itemCount} productos`

    const handleCopyTracking = () => {
        navigator.clipboard.writeText(order.tracking_number)
    }

    return (
        <div className={styles.container}>
            {/* Back button */}
            <button className={styles.backButton} onClick={() => navigate(ROUTES.orders)}>
                <RiArrowLeftLine /> Volver a pedidos
            </button>

            {/* Header: order number + date + status badge */}
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>{order.order_number}</h1>
                    <p className={styles.date}>{new Date(order.created).toLocaleString('es-PE')}</p>
                </div>
                <OrderStatusBadge status={order.status} />
            </div>

            {/* Progress bar */}
            <div className={styles.progressSection}>
                <OrderProgressBar status={order.status} />
            </div>

            {/* Items list */}
            <div className={styles.card}>
                <h3>
                    <RiBox3Line className={styles.sectionIcon} />
                    Productos ({itemLabel})
                </h3>
                <div className={styles.itemsList}>
                    {order.items.map((item) => (
                        <ItemCard key={item.id} item={item} />
                    ))}
                </div>
            </div>

            {/* Price summary */}
            <div className={styles.card}>
                <h3>Resumen de pago</h3>
                <div className={styles.detail}>
                    <span>Productos</span>
                    <span>{formatSoles(order.items_total)}</span>
                </div>
                <div className={styles.detail}>
                    <span>Envío</span>
                    <span>{formatSoles(order.shipping_cost)}</span>
                </div>
                <div className={`${styles.detail} ${styles.totalRow}`}>
                    <span>Total</span>
                    <span>{formatSoles(order.total)}</span>
                </div>

                {/* Payment method */}
                {order.payment_method && (
                    <div className={styles.paymentInfo}>
                        <RiBankCard2Line className={styles.paymentIcon} />
                        <div className={styles.paymentDetails}>
                            <span className={styles.paymentLabel}>Método de pago</span>
                            <span className={styles.paymentMethod}>{order.payment_method}</span>
                            {order.izipay_transaction_id && (
                                <span className={styles.transactionId}>
                                    Ref: {order.izipay_transaction_id}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Tracking number */}
            {order.tracking_number && (
                <div className={styles.card}>
                    <h3>Seguimiento</h3>
                    <div className={styles.trackingBox}>
                        <RiMapPinLine />
                        <div>
                            <span className={styles.trackingLabel}>N° de seguimiento</span>
                            <span className={styles.trackingNumber}>{order.tracking_number}</span>
                        </div>
                        <button
                            className={styles.copyButton}
                            onClick={handleCopyTracking}
                            title="Copiar número de seguimiento"
                        >
                            <RiFileCopyLine />
                            Copiar
                        </button>
                    </div>
                </div>
            )}

            {/* Pending order — paid, awaiting admin confirmation. Under the
                order-on-payment model an order only exists once the payment
                confirmed, so there is no "complete payment" path here; the
                buyer can still cancel while it is pending. */}
            {order.status === 'pending' && (
                <div className={styles.actionCard}>
                    <p className={styles.actionText}>
                        Tu pago fue recibido. Estamos revisando tu pedido y lo confirmaremos pronto.
                    </p>
                    <div className={styles.actionButtons}>
                        {!showCancelConfirm ? (
                            <Button
                                variant="secondary"
                                onClick={() => setShowCancelConfirm(true)}
                            >
                                <RiCloseLine /> Cancelar pedido
                            </Button>
                        ) : (
                            <div className={styles.cancelConfirm}>
                                <p className={styles.cancelText}>¿Seguro que quieres cancelar este pedido?</p>
                                <div className={styles.cancelActions}>
                                    <Button
                                        variant="danger"
                                        onClick={() => {
                                            cancelOrder.mutate(order.uuid, {
                                                onSuccess: () => {
                                                    toast.success('Pedido cancelado')
                                                    setShowCancelConfirm(false)
                                                },
                                                onError: () => toast.error('Error al cancelar'),
                                            })
                                        }}
                                        disabled={cancelOrder.isPending}
                                    >
                                        {cancelOrder.isPending ? <Spinner size="sm" /> : 'Sí, cancelar'}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => setShowCancelConfirm(false)}
                                    >
                                        No, volver
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Timeline section */}
            <div className={styles.card}>
                <h3>Historial de estado</h3>
                <OrderTimeline logs={order.status_logs} />
            </div>
        </div>
    )
}
